import React, { useEffect } from 'react';
import { orderBy } from 'lodash';
import { classNames } from '@fullstory/util/fmt';
import {
  IconButton,
  Flex,
  Box,
  ActionMenu,
  Table,
  useSorting,
  Typography,
  SortDirection,
  TableColumnType,
  Icon,
} from '@fullstory/components';
import { ResourceListItemDescription } from '../ResourceListItemDescription';
import css from './index.module.scss';
import { timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import { useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';
import { ItemType, Action, LibrarySortState } from '../../../types';
import { useHistory, useLocation } from 'react-router-dom';
import { SORT_QUERY_PARAM } from '../../../constants';

export const formatLastUpdated = ({
  lastUpdated,
  lastUpdatedBy,
}: {
  lastUpdated: timestamp | null;
  lastUpdatedBy: string;
}): string => {
  if (!lastUpdatedBy) return '';

  const name = lastUpdatedBy === 'system' ? 'FullStory' : lastUpdatedBy;

  if (!lastUpdated) return name;

  return intl.formatMessage(
    {
      defaultMessage: '{lastUpdated, date, long}, {name}',
      id: 'hBnbyL',
      description:
        'Displays the date a resource was last updated and who updated it in a table cell',
    },
    { lastUpdated: new Date(lastUpdated as string), name },
  );
};

const menuActions = [
  {
    id: 'view',
    label: intl.formatMessage({
      defaultMessage: 'View detail',
      id: 'P808PK',
      description: 'View detail action for a resource',
    }),
    iconName: 'search-plus',
  },
  {
    id: 'duplicate',
    label: intl.formatMessage({
      defaultMessage: 'Duplicate',
      id: 'Iyhr/6',
      description: 'Duplicate action for a resource',
    }),
    iconName: 'duplicate',
  },
  {
    id: 'archive',
    label: intl.formatMessage({
      defaultMessage: 'Archive',
      id: 'dbPVuu',
      description: 'Archive action for a resource',
    }),
    iconName: 'folder',
  },
  {
    id: 'delete',
    label: intl.formatMessage({
      defaultMessage: 'Delete',
      id: 'KPA3a8',
      description: 'Delete action for a resource',
    }),
    iconName: 'trash',
  },
] as const;

type MenuAction = Exclude<Action, 'star'>;

const defaultActions: Action[] = ['view', 'duplicate', 'archive', 'star', 'delete'];
export interface ResourceListTableProps<I extends ItemType> {
  items: I[];
  icon: React.ComponentPropsWithoutRef<typeof Icon>['name'];
  disabled?: boolean;
  onAction?: (action: { id: string; action: Action }) => void;
  disableSorting?: boolean;
  getPagedData?: (items: I[]) => I[];
  middleColumns?: TableColumnType<I>[];
  sortFunction?: (rows: I[], key: string, direction: SortDirection) => I[];
  defaultSort?: LibrarySortState;
  shouldSetSortQueryParams?: boolean;
}

export function ResourceListTable<I extends ItemType>({
  items,
  icon,
  disabled = false,
  disableSorting = false,
  onAction = () => {},
  getPagedData = items => items,
  middleColumns,
  sortFunction,
  defaultSort,
  shouldSetSortQueryParams,
}: ResourceListTableProps<I>) {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const sortingProps = useSorting(items, defaultSort?.sortColumnKey ?? 'lastModified', {
    defaultSortDirection: defaultSort?.direction ?? 'desc',
    sortFunction: (itemList: I[], key: string, direction: SortDirection) => {
      if (disableSorting) {
        return itemList;
      } else if (sortFunction) {
        return sortFunction(itemList, key, direction);
      } else {
        switch (key) {
          case 'lastModified':
            return orderBy(
              itemList,
              item =>
                item.lastUpdated == null ? new Date(0) : Timestamp.mustDate(item.lastUpdated),
              direction,
            );
          default:
            return orderBy(itemList, key, direction);
        }
      }
    },
  });

  const { activeSortColumnKey, activeSortDirection } = sortingProps;

  useEffect(() => {
    if (shouldSetSortQueryParams) {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set(SORT_QUERY_PARAM, `${activeSortColumnKey}:${activeSortDirection}`);
      history.replace({ pathname: location.pathname, search: `?${queryParams.toString()}` });
    }
  }, [
    shouldSetSortQueryParams,
    activeSortColumnKey,
    activeSortDirection,
    history,
    location.pathname,
    location.search,
  ]);

  const onClick = disabled
    ? undefined
    : (item: ItemType) => {
        onAction({ id: item.id, action: 'view' });
      };

  return (
    <Table
      data-component="ResourceListTable"
      {...sortingProps}
      rows={getPagedData(sortingProps.rows)}
      className={css.table}
      fixed
      dense
      headProps={{ variant: 'dark', rowProps: { cellProps: { fixed: true } } }}
      bodyProps={{ rowProps: { onClick } }}
      rowKeyAccessor={item => item.id}
      columns={[
        {
          key: 'name',
          label: intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' }),
          align: 'left',
          sortable: !disableSorting,
          minWidth: 200,
          renderCell(item) {
            const itemActions = item.actions ?? defaultActions;
            const onStar = itemActions.includes('star')
              ? () => onAction({ id: item.id, action: 'star' })
              : undefined;
            return (
              <ResourceListItemDescription
                name={item.name}
                description={item.description}
                disabled={disabled}
                starred={item.starred}
                onStar={onStar}
                icon={icon}
                rightAdornment={item.rightAdornment}
              />
            );
          },
        },
        ...(middleColumns ?? []),
        {
          key: 'lastModified',
          label: intl.formatMessage({ defaultMessage: 'Last modified', id: '1Jufsz' }),
          align: 'left',
          minWidth: 300,
          sortable: !disableSorting,
          renderCell(item) {
            const itemActions = item.actions ?? defaultActions;
            const itemMenuActions = menuActions.filter(menuAction =>
              itemActions.includes(menuAction.id),
            );
            const archiveItemAction = itemActions.find(action => 'archive' === action);
            return (
              <Flex justifyContent="space-between">
                <Typography
                  ellipsize
                  variant="body3"
                  lineHeight="24px"
                  className={classNames({ [css.disabled]: disabled })}
                >
                  {formatLastUpdated(item)}
                </Typography>
                <Box paddingLeft={16}>
                  {item.archived
                    ? archiveItemAction && (
                        <IconButton
                          testId="archive-icon-button"
                          title={intl.formatMessage({
                            defaultMessage: 'Restore',
                            id: 'jE3mmK',
                            description: 'Restore action for a resource',
                          })}
                          className="archive-icon"
                          iconName="folder"
                          onClick={e => {
                            onAction({ id: item.id, action: 'archive' });
                            e.stopPropagation();
                          }}
                        />
                      )
                    : itemMenuActions.length > 0 && (
                        <ActionMenu
                          actions={itemMenuActions}
                          onSelect={id =>
                            onAction({
                              id: item.id,
                              action: id as MenuAction,
                            })
                          }
                        />
                      )}
                </Box>
              </Flex>
            );
          },
        },
      ]}
    />
  );
}
